/**
 * Recovery code: 256-bit entropy ↔ 24-word BIP-39 (English).
 *
 * Reuses `@scure/bip39` (entropy↔mnemonic, checksum, validation) and the
 * canonical English wordlist — nothing re-implemented. The bundled wordlist is
 * pinned to its canonical SHA-256 and verified once before first use; a mismatch
 * (supply-chain tampering) throws {@link WordlistIntegrityError}.
 *
 * Privacy: on a malformed code we reject with a generic word/checksum error and
 * offer NO edit-distance suggestion (spec §6).
 */
import { entropyToMnemonic, mnemonicToEntropy, generateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import { RecoveryCodeChecksumError, RecoveryCodeWordError, RecoveryCodeLengthError, WordlistIntegrityError, } from './errors.js';
import { toArrayBuffer } from './bytes.js';
/**
 * SHA-256 of the canonical English wordlist, newline-joined with a trailing
 * newline (the widely-published reference fingerprint).
 */
const CANONICAL_WORDLIST_SHA256 = '2f5eed53a4727b4bf8880d8f3f199efc90e58503646d9ff8eff3a2ed3b24dbda';
const ENTROPY_BYTES = 32; // 256-bit → 24 words
let integrityChecked = false;
const wordSet = new Set(wordlist);
function toHex(bytes) {
    let s = '';
    for (const b of bytes) {
        s += b.toString(16).padStart(2, '0');
    }
    return s;
}
/** Verify the bundled wordlist once. Idempotent; throws on tamper. */
async function ensureWordlistIntegrity() {
    if (integrityChecked) {
        return;
    }
    if (wordlist.length !== 2048) {
        throw new WordlistIntegrityError();
    }
    const joined = new TextEncoder().encode(wordlist.join('\n') + '\n');
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', toArrayBuffer(joined)));
    if (toHex(digest) !== CANONICAL_WORDLIST_SHA256) {
        throw new WordlistIntegrityError();
    }
    integrityChecked = true;
}
function normalise(words) {
    return words
        .trim()
        .toLowerCase()
        .split(/\s+/u)
        .filter((w) => w.length > 0);
}
/** Generate a fresh 24-word recovery code from 256 bits of entropy. */
export async function generateRecoveryCode() {
    await ensureWordlistIntegrity();
    // 256-bit strength → 24 words. @scure draws its own CSPRNG entropy.
    return generateMnemonic(wordlist, 256);
}
/** Encode 32 bytes of entropy to a 24-word recovery code. */
export async function entropyToRecoveryCode(entropy) {
    await ensureWordlistIntegrity();
    if (entropy.length !== ENTROPY_BYTES) {
        throw new RecoveryCodeChecksumError();
    }
    return entropyToMnemonic(entropy, wordlist);
}
/**
 * Decode a 24-word recovery code to its 32-byte entropy. Validates each word
 * against the wordlist first (E_BIP39_WORD), then the checksum (E_BIP39_CHECKSUM).
 */
export async function recoveryCodeToEntropy(words) {
    await ensureWordlistIntegrity();
    const parts = normalise(words);
    if (parts.length !== 24) {
        throw new RecoveryCodeLengthError();
    }
    for (const w of parts) {
        if (!wordSet.has(w)) {
            throw new RecoveryCodeWordError();
        }
    }
    try {
        return mnemonicToEntropy(parts.join(' '), wordlist);
    }
    catch {
        // Length (24) and every word are validated above, so the only remaining
        // failure mode here is the BIP-39 checksum.
        throw new RecoveryCodeChecksumError();
    }
}
//# sourceMappingURL=bip39.js.map